import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/Components/ui/dialog';
import { Button } from '@/Components/ui/button';
import { Card } from '@/Components/ui/card';
import { Input } from '@/Components/ui/input';
import { Badge } from '@/Components/ui/badge';
import { Text } from '@/Components/ui/typography';
import { Check, Clock3, Loader2, Mail, Send, UserRound, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getApiUrl } from '../../../config/api';
import { getColor } from '@/config/theme';
import { toast } from 'sonner';

const getStoredMeetingOwnerEmail = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return (
    sessionStorage.getItem('employer_email') ||
    localStorage.getItem('employer_email') ||
    sessionStorage.getItem('admin_email') ||
    localStorage.getItem('admin_email') ||
    sessionStorage.getItem('recruiter_email') ||
    localStorage.getItem('recruiter_email') ||
    ''
  ).trim().toLowerCase();
};

/** Canonical automated Meeting slot-sending dialog (Google Meet / Live Meet). */
export default function MeetingSchedulingDialog({
  candidate,
  jobDetails,
  meetingSetup,
  initialMeetingProvider = 'google_meet',
  selectedCandidates = [],
  onBack,
  onClose,
}) {
  const [selectedSlots, setSelectedSlots] = useState(
    () => new Set((meetingSetup?.slots || []).map((slot) => slot.value))
  );
  const [meetingProvider, setMeetingProvider] = useState(
    initialMeetingProvider === 'live_meet' ? 'live_meet' : 'google_meet'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    const def = meetingSetup?.meetingProvider || meetingSetup?.availability?.meeting_provider_default;
    if (def === 'search_job_meet' || def === 'live_meet') {
      setMeetingProvider('live_meet');
    } else if (def === 'google_meet') {
      setMeetingProvider('google_meet');
    }
  }, [meetingSetup?.meetingProvider, meetingSetup?.availability?.meeting_provider_default]);

  const candidateName = candidate?.name || candidate?.full_name || candidate?.candidate_email || 'Candidate';
  const candidateEmail = candidate?.candidate_email || candidate?.email || '';
  const interviewer = meetingSetup?.interviewer || null;
  const slots = meetingSetup?.slots || [];
  const normalizedSelectedCandidates = [
    {
      candidate_email: candidateEmail,
      candidate_name: candidateName,
      job_id: candidate?.job_id || jobDetails?.job_id || '',
    },
    ...(selectedCandidates || [])
      .filter(Boolean)
      .map((item) => ({
        candidate_email: item?.candidate_email || item?.email || '',
        candidate_name: item?.name || item?.full_name || item?.candidate_email || item?.email || 'Candidate',
        job_id: item?.job_id || jobDetails?.job_id || '',
      })),
  ]
    .filter((item, index, array) => item.candidate_email && array.findIndex((entry) => entry.candidate_email === item.candidate_email) === index);
  const selectedSlotDetails = useMemo(
    () => slots.filter((slot) => selectedSlots.has(slot.value)),
    [slots, selectedSlots]
  );
  const canSendSlots = Boolean(slots.length && normalizedSelectedCandidates.length && selectedSlotDetails.length && !isSubmitting);
  const sendButtonLabel = isSubmitting
    ? 'Sending Slot Options...'
    : normalizedSelectedCandidates.length > 1
      ? `Send to ${normalizedSelectedCandidates.length} Candidates`
      : 'Send Slot Options';

  const toggleSlot = (slotValue) => {
    setSelectedSlots((previous) => {
      const next = new Set(previous);
      if (next.has(slotValue)) {
        next.delete(slotValue);
      } else {
        next.add(slotValue);
      }
      return next;
    });
  };

  const handleSendSlots = async () => {
    const chosenSlots = selectedSlotDetails;

    if (!normalizedSelectedCandidates.length) {
      setSubmitError('No selected candidate is available for slot sending.');
      return;
    }

    if (!chosenSlots.length) {
      setSubmitError('Select at least one slot to send to the candidate.');
      return;
    }
    if (!companyName.trim()) {
      setSubmitError('Company name is required.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await axios.post(getApiUrl('/api/meeting/schedule/request'), {
        candidate_email: candidateEmail,
        candidate_name: candidateName,
        job_id: candidate?.job_id || jobDetails?.job_id || '',
        candidates: normalizedSelectedCandidates,
        interviewer_email: interviewer?.email,
        interviewer_name: interviewer?.name,
        slot_options: chosenSlots,
        created_by_email: getStoredMeetingOwnerEmail(),
        meeting_provider: meetingProvider,
        company_name: companyName.trim(),
      });

      setSubmitSuccess(response.data);
      toast.success('Meeting slots sent to candidate.');
    } catch (requestError) {
      const message = requestError.response?.data?.error || 'Failed to send meeting slots.';
      setSubmitError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[88vh] max-w-2xl flex-col overflow-hidden p-0">
        <DialogHeader>
          <div className="border-b bg-gradient-to-br from-slate-50 via-white to-sky-50/70 px-4 pb-4 pt-5 sm:px-5">
            <DialogTitle className="text-xl">Candidate Meeting Scheduling</DialogTitle>
            <DialogDescription className="mt-2 max-w-2xl text-sm">
              Prepare polished slot options for <strong>{candidateName}</strong> and send them to <strong>{candidateEmail || 'the candidate'}</strong>. The first confirmed slot will lock automatically and trigger the final meeting invite for both sides.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="grid flex-1 gap-4 overflow-y-auto px-4 pb-4 pt-4 sm:px-5 sm:pb-5 sm:pt-4">
          <div className="min-w-0 space-y-5">
            <Card className="min-w-0 overflow-hidden border-slate-200 bg-white shadow-sm">
              <div className="border-b bg-slate-50/80 px-4 py-3">
                <Text className="font-semibold text-slate-900">Scheduling Overview</Text>
              </div>
              <div className="grid gap-3 p-4 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="mb-3 flex items-center gap-2 text-slate-700">
                    <UserRound className="h-4 w-4" />
                    <Text size="sm" className="font-medium">Primary Candidate</Text>
                  </div>
                  <Text className="font-semibold text-slate-900">{candidateName}</Text>
                  <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="h-4 w-4" />
                    <span>{candidateEmail || 'Candidate email unavailable'}</span>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="mb-3 flex items-center gap-2 text-slate-700">
                    <Users className="h-4 w-4" />
                    <Text size="sm" className="font-medium">Interviewer</Text>
                  </div>
                  <Text className="font-semibold text-slate-900">{interviewer?.name || 'Verified Interviewer'}</Text>
                  <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="h-4 w-4" />
                    <span>{interviewer?.email || 'Email unavailable'}</span>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="min-w-0 overflow-hidden border-slate-200 bg-white shadow-sm">
              <div className="border-b bg-slate-50/80 px-4 py-3">
                <Text className="font-semibold text-slate-900">
                  Company name <span className="text-red-500">*</span>
                </Text>
              </div>
              <div className="p-4">
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Enter your company name"
                  required
                />
              </div>
            </Card>

            <Card className="min-w-0 overflow-hidden border-slate-200 bg-white shadow-sm">
              <div className="border-b bg-slate-50/80 px-4 py-3">
                <Text className="font-semibold text-slate-900">Video platform</Text>
                <Text size="sm" variant="muted" className="mt-1">
                  Choose how the interview will be hosted when the candidate confirms a slot.
                </Text>
              </div>
              <div className="space-y-3 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50">
                    <input
                      type="radio"
                      name="meeting-provider"
                      className="h-4 w-4 accent-sky-600"
                      checked={meetingProvider === 'google_meet'}
                      onChange={() => setMeetingProvider('google_meet')}
                    />
                    <span className="text-sm font-medium text-slate-800">Google Meet</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50">
                    <input
                      type="radio"
                      name="meeting-provider"
                      className="h-4 w-4 accent-sky-600"
                      checked={meetingProvider === 'live_meet'}
                      onChange={() => setMeetingProvider('live_meet')}
                    />
                    <span className="text-sm font-medium text-slate-800">Search Job Meet</span>
                  </label>
                </div>
                <Text size="xs" variant="muted">
                  Interviewer default:{' '}
                  {['search_job_meet', 'live_meet'].includes(meetingSetup?.availability?.meeting_provider_default)
                    ? 'Search Job Meet'
                    : 'Google Meet'}
                </Text>
              </div>
            </Card>

            <Card className="min-w-0 overflow-hidden border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-3 border-b bg-slate-50/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <Text className="font-semibold text-slate-900">Available Slot Options</Text>
                  <Text size="sm" variant="muted" className="mt-1">
                    Select one or more slots to share. Candidates will receive only the options you choose here.
                  </Text>
                </div>
                <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
                  {slots.length} slots ready
                </Badge>
              </div>
              <div className="max-h-[280px] space-y-2 overflow-y-auto p-4">
                {slots.length ? slots.map((slot, index) => {
                  const isSelected = selectedSlots.has(slot.value);
                  return (
                    <button
                      key={slot.value}
                      type="button"
                      onClick={() => toggleSlot(slot.value)}
                      className={cn(
                        'w-full rounded-xl border px-3 py-3 text-left transition-all',
                        isSelected
                          ? 'border-sky-300 bg-sky-50/80 shadow-sm ring-1 ring-sky-100'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/70'
                      )}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className={cn(
                              'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold',
                              isSelected ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-700'
                            )}>
                              {index + 1}
                            </div>
                            <Text className="font-semibold text-sm text-slate-900">{slot.label}</Text>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-600">
                            <Clock3 className="h-4 w-4" />
                            <span>{isSelected ? 'Ready to share with selected candidates' : 'Click to include this option in the candidate email'}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isSelected ? (
                            <Badge className="border-sky-200 bg-sky-600 text-white hover:bg-sky-600">
                              Selected
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-slate-200 text-slate-600">
                              Available
                            </Badge>
                          )}
                          {isSelected && <Check className="h-5 w-5 text-sky-600" />}
                        </div>
                      </div>
                    </button>
                  );
                }) : (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                    <Text className="font-medium text-slate-800">No slots available to send</Text>
                    <Text size="sm" variant="muted" className="mt-1">
                      Go back to the interviewer setup and refresh availability if needed.
                    </Text>
                  </div>
                )}
              </div>
            </Card>
          </div>

          <div className="min-w-0 space-y-4">
            <Card className="min-w-0 overflow-hidden border-slate-200 bg-white shadow-sm">
              <div className="border-b bg-slate-50/80 px-4 py-3">
                <Text className="font-semibold text-slate-900">Selected Candidates</Text>
              </div>
              <div className="space-y-3 p-4">
                <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Users className="h-4 w-4" />
                    <Text size="sm" className="font-medium">Candidate Count</Text>
                  </div>
                  <Badge variant="outline" className={cn(
                    normalizedSelectedCandidates.length
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-amber-200 bg-amber-50 text-amber-700'
                  )}>
                    {normalizedSelectedCandidates.length || 0}
                  </Badge>
                </div>

                {normalizedSelectedCandidates.length ? (
                  <div className="max-h-[140px] space-y-2 overflow-y-auto pr-1">
                    {normalizedSelectedCandidates.map((item) => (
                      <div key={item.candidate_email} className="min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-3">
                        <div className="min-w-0">
                          <Text className="font-medium text-sm text-slate-900">{item.candidate_name}</Text>
                          <Text size="sm" variant="muted" className="mt-1 break-all">{item.candidate_email}</Text>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4">
                    <Text className="font-medium text-amber-800">No selected candidates</Text>
                    <Text size="sm" className="mt-1 text-amber-700">
                      Select at least one candidate before sending slot options.
                    </Text>
                  </div>
                )}
              </div>
            </Card>

            <Card className="min-w-0 overflow-hidden border-slate-200 bg-white shadow-sm">
              <div className="border-b bg-slate-50/80 px-4 py-3">
                <Text className="font-semibold text-slate-900">Delivery Summary</Text>
              </div>
              <div className="space-y-3 p-4">
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                  <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                    <Text size="sm" variant="muted">Selected Slots</Text>
                    <Text className="mt-1 text-lg font-semibold text-slate-900">{selectedSlotDetails.length}</Text>
                  </div>
                  <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                    <Text size="sm" variant="muted">Email Delivery</Text>
                    <Text className="mt-1 text-lg font-semibold text-slate-900">
                      {normalizedSelectedCandidates.length ? 'Ready' : 'Blocked'}
                    </Text>
                  </div>
                </div>

                {selectedSlotDetails.length ? (
                  <div className="space-y-2">
                    <Text size="sm" className="font-medium text-slate-800">Slots that will be shared</Text>
                    <div className="max-h-[120px] space-y-2 overflow-y-auto pr-1">
                      {selectedSlotDetails.map((slot) => (
                        <div key={slot.value} className="min-w-0 rounded-xl border border-sky-100 bg-sky-50/70 px-3 py-2 text-xs text-slate-700 break-words">
                          {slot.label}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4">
                    <Text size="sm" className="text-slate-600">
                      Choose at least one slot to unlock the send action.
                    </Text>
                  </div>
                )}

                <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-4">
                  <Text size="sm" className="font-medium text-slate-800">What happens next</Text>
                  <Text size="sm" variant="muted" className="mt-1">
                    Selected candidates receive the chosen slot options by email. The first confirmed response reserves the slot and sends final meeting details to both candidate and interviewer.
                  </Text>
                </div>

                {submitError && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                    <Text size="sm" className="font-medium text-red-800">Unable to send slot options</Text>
                    <Text size="sm" className="mt-1 text-red-700">{submitError}</Text>
                  </div>
                )}

                {submitSuccess && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                    <Text size="sm" className="font-medium text-emerald-800">
                      Slot options sent successfully to {normalizedSelectedCandidates.length} selected candidate(s).
                    </Text>
                    <Text size="sm" className="mt-1 text-emerald-700">
                      Candidates can now review the available options from email and confirm their preferred interview time.
                    </Text>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>

        <DialogFooter className="border-t bg-slate-50/70 px-4 py-4 sm:px-6 sm:justify-between">
          <Button variant="outline" onClick={onBack || onClose}>
            {onBack ? 'Back' : 'Close'}
          </Button>
          <Button
            onClick={handleSendSlots}
            disabled={!canSendSlots}
            style={{
              backgroundColor: getColor('primary'),
              color: getColor('primaryForeground'),
            }}
            className="min-w-[200px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {sendButtonLabel}
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                {sendButtonLabel}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
